<template>
  <RoleControlledAction
    v-slot="{restricted, wrapper}"
    :user-role="roleName"
    :config-object="roleConfig.cancelOrders"
  >
    <button
      :class="{ 'custom-disabled': !isCancellable || restricted || !!props.pendingOperation}"
      class="btn btn-block btn-sm btn-ghost text-left"
      @click="wrapper(()=> initCancelOrder())"
    >
      {{ buttonCopy }}
    </button>
  </RoleControlledAction>

  <TbDialog
    :show="cancelOrderDialog"
    @close="closeCancelOrder"
  >
    <template #header>
      <h2 class="text-xl font-bold">
        Cancel {{ order?.product }}
      </h2>
    </template>

    <p>
      You're about to cancel an order for <strong class="font-bold">{{ order.studentName?.toUpperCase() }}</strong>
    </p>

    <br>

    <p>
      Once confirmed, we will cancel all future payments and no refunds will take place.
    </p>

    <p v-if="order.origin === 'lnpl' && cancellationAmount">
      <br>

      Your next eligible payout will be adjusted by
      <strong class="font-semibold">
        {{ `-${currencyInCents(cancellationAmount)}` }}
      </strong>.
    </p>

    <InsufficientFundsWarning
      v-if="isFundedCancellation"
      class="mt-4"
      type="cancellation"
      :merchant-payment-method="merchantAccountLastFourDigits"
      :amount-in-cents="cancellationAmount"
    />

    <div class="mt-6">
      <label
        for="cancellation-reason"
        class="label"
      >
        Reason (required)
      </label>

      <textarea
        id="cancellation-reason"
        v-model="cancellationReason"
        class="block w-full input mt-2 resize-none"
      />
    </div>

    <p
      v-if="order.origin === 'lnpl'"
      class="mt-5 text-gray-400 font-light"
    >
      To learn more about how we calculate cancellation adjustments, check out our help centre article
      <a
        target="_blank"
        href="https://help.elective.com/hc/en-us/articles/10400657851547"
      >
        here</a>.
    </p>

    <p
      v-if="notFoundError"
      class="mt-2 text-error text-center"
    >
      To complete this action, please provide your payment information. Visit your <router-link
        :to="{ name: Pages.paymentDetails }"
      >
        settings
      </router-link> to submit your payment details and try again.
    </p>
    <p
      v-else-if="cancelError"
      class="mt-2 text-error text-center"
    >
      {{ cancelError }}
    </p>

    <template #footer>
      <div>
        <div class="flex gap-4 actions">
          <button
            class="btn btn-sm btn-ghost"
            @click="closeCancelOrder"
          >
            No, I’ve changed my mind
          </button>

          <button
            class="btn btn-sm btn-primary"
            :disabled="isLoading.valueOf() || !cancellationReason.trim() || hasPendingOperation"
            @click="submitCancellation"
          >
            <TbIcon
              v-if="isLoading"
              icon="spinner"
              class="text-xl text-white animate-spin"
            />
            <div v-else>
              Confirm Cancellation
            </div>
          </button>
        </div>
      </div>
    </template>
  </TbDialog>
</template>

<script lang="ts" setup>
import {
  ref, inject, Ref, computed,
} from 'vue';
import { useRoute } from 'vue-router';
import { TbDialog, TbIcon } from '@/components/tasty_bistro';
import { currencyInCents } from '@/filters/currency';
import { Pages } from '@/router';
import RoleControlledAction from '@/components/role_controlled_action.vue';
import { ProjectState } from '@/pages/app/project/api/get_project_state';
import { cancelOrder } from '../api/cancel';
import { getCancellationData } from '../api/get_cancellation_data';
import { ExistingFundedOperation, Order } from '../api/get_orders';
import { roleConfig } from '../role_configurations';
import InsufficientFundsWarning from './components/insufficient_funds_warning.vue';

const projectState = inject<Ref<ProjectState>>('project') as Ref<ProjectState>;

const route = useRoute();
const projectId = route.params.projectId as string;
const roleName = projectState.value.currentUserRoleName;

const props = defineProps<{
  order: Order,
  isCancellable: boolean,
  pendingOperation: ExistingFundedOperation | null
}>();
const emit = defineEmits([
  'cancelOrder',
]);

const buttonCopy = computed(() => {
  const { pendingOperation } = props;
  if (!pendingOperation || !pendingOperation.type) {
    return 'Cancel Order';
  }

  if (pendingOperation.type === 'cancellation') {
    return 'Cancel Order (in progress)';
  }

  return 'Cancel Order';
});

const cancelError = ref<String>('');
const notFoundError = ref<Boolean>(false);
const cancelOrderDialog = ref<Boolean>(false);
const isLoading = ref<Boolean>(false);
const cancellationAmount = ref<number>(0);
const cancellationReason = ref<string>('');
const isFundedCancellation = ref<boolean>(false);
const merchantAccountLastFourDigits = ref<string>('');
const hasPendingOperation = ref<boolean>(false);

const initCancelOrder = async () => {
  if (!props.isCancellable || !!props.pendingOperation) {
    return;
  }

  try {
    if (props.order.origin === 'lnpl') {
      const data = await getCancellationData({ orderId: props.order.id, projectId });
      cancellationAmount.value = data.cancellationAmountInCents;
      isFundedCancellation.value = data.isFundedCancellation;
      merchantAccountLastFourDigits.value = data.merchantAccountLastFourDigits;
      hasPendingOperation.value = data.hasPendingOperation;
    }
  } catch (error) {
    const err = error as any;
    if (err.response?.data?.type === 'NotFound') {
      notFoundError.value = true;
    } else {
      cancelError.value = 'Error while getting cancellation data. Please contact support.';
    }
  } finally {
    cancelOrderDialog.value = true;
  }
};

const closeCancelOrder = () => {
  cancelOrderDialog.value = false;
  notFoundError.value = false;
  cancelError.value = '';
};

const submitCancellation = async () => {
  try {
    isLoading.value = true;
    notFoundError.value = false;
    cancelError.value = '';

    await cancelOrder({
      orderId: props.order.id, reason: cancellationReason.value, isFundedCancellation: isFundedCancellation.value, projectId,
    });

    cancelOrderDialog.value = false;
    emit('cancelOrder');
  } catch (error) {
    const err = error as any;
    if (err.type) {
      cancelError.value = err.message;
    } else {
      cancelError.value = 'Error processing request. Please contact support.';
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
  .custom-disabled {
    @apply cursor-not-allowed opacity-[0.35];
  }
</style>
